/**
 * Modelo do gráfico de um instrumento (M6) — GUPM-211. TS puro, testável sem mock.
 *
 * Junta a série do instrumento (cota), a régua do reservatório e a pluviometria
 * (overlays opcionais) num único conjunto de pontos alinhado por data, calcula as
 * linhas de referência e o domínio Y "inteligente" (porte do `_m6YRange` do V8) e
 * sinaliza a vigência da Carta de Controle (R-A07).
 */
import type { Instrument, Reading } from '@/domain';
import type { FaixaCanal } from '@/domain/instrumentos/tilt';
import {
  toChartSeries, buildReferenceLines, TYPE_UNIT, type ReferenceLineSpec,
} from './chart-data';

/** Ponto do gráfico — cada dimensão é `null` quando não há dado naquela data. */
export interface ChartSeriesPoint {
  date: string;
  ts: number;
  /** Leitura do instrumento (eixo de cota). */
  value: number | null;
  /** Régua do reservatório (eixo secundário, m). */
  regua: number | null;
  /** Pluviometria acumulada (barras, mm). */
  pluviometria: number | null;
  /**
   * GUPM-270 — eixo B do tiltímetro (2ª série, mesma escala de graus do eixo A).
   * `null` para instrumento de canal único.
   */
  eixoB: number | null;
}

export interface ChartModel {
  points: ChartSeriesPoint[];
  referenceLines: ReferenceLineSpec[];
  /** Domínio inicial do eixo de cota (ajustado à leitura) — base do zoom Y. */
  cotaDomain: [number, number];
  /** Domínio máximo (fundo→boca + todos os níveis) — trava do zoom-out. */
  fullCotaDomain: [number, number];
  hasRegua: boolean;
  hasPluviometria: boolean;
  /** GUPM-270 — o gráfico tem 2ª série (eixo B do tiltímetro). */
  hasEixoB: boolean;
  /** Série cruza anos → o rótulo do X inclui o ano (evita `dd/MM` ambíguo). */
  multiYear: boolean;
  /** R-A07 — `false` quando a leitura mais recente é anterior à vigência dos limiares. */
  thresholdsVigentes: boolean;
  unit: string;
}

/**
 * Janela inicial do Brush (zoom X) — mostra os últimos `days` dias (padrão: 1 semana).
 * Índices sobre `points` (já ordenados por ts). A barra segue arrastável pra ampliar.
 */
export function initialBrushRange(
  points: { ts: number }[],
  days = 7,
): { startIndex: number; endIndex: number } {
  const n = points.length;
  if (n === 0) return { startIndex: 0, endIndex: 0 };
  const endIndex = n - 1;
  const cutoff = points[endIndex]!.ts - days * 86_400_000;
  let startIndex = 0;
  for (let i = endIndex; i >= 0; i--) {
    if (points[i]!.ts < cutoff) {
      startIndex = i + 1;
      break;
    }
  }
  return { startIndex: Math.min(startIndex, endIndex), endIndex };
}

/** Rótulo de data do eixo X — `dd/MM`, com ano quando a série cruza anos. Puro. */
export function dateLabel(ts: number, multiYear: boolean): string {
  const d = new Date(ts);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return multiYear ? `${dd}/${mm}/${String(d.getFullYear()).slice(2)}` : `${dd}/${mm}`;
}

/**
 * Descrição acessível do gráfico (o SVG do Recharts não é lido por leitor de tela).
 * Puro — deriva do modelo. Lista as referências e sinaliza overlays/vigência.
 */
export function chartAriaLabel(model: ChartModel): string {
  const refs = model.referenceLines.map((l) => l.label).join(', ');
  const extras = [
    model.hasRegua ? 'régua do reservatório sobreposta' : '',
    model.hasPluviometria ? 'pluviometria em barras' : '',
    model.thresholdsVigentes ? '' : 'limiares fora de vigência',
  ].filter(Boolean).join('; ');
  return [
    `Gráfico de série temporal da leitura (${model.unit || 'un.'}).`,
    refs ? `Linhas de referência: ${refs}.` : '',
    extras ? `${extras}.` : '',
  ].filter(Boolean).join(' ');
}

export interface BuildChartModelInput {
  instrument: Instrument;
  series: Reading[];
  /** Leituras da régua do reservatório (instrumento RL da mesma barragem). */
  reguaSeries?: Reading[];
  /** Leituras de pluviometria (instrumento EM/PLV da mesma barragem). */
  pluviSeries?: Reading[];
  /** GUPM-270 — leituras do eixo B do tiltímetro (`series` passa a ser o eixo A). */
  seriesEixoB?: Reading[];
  /**
   * GUPM-270 — faixa vigente do canal, fonte das linhas de referência do
   * tiltímetro. Os campos legados `levelAtencaoMax` do instrumento são nulos para
   * TILT: sem isto o gráfico sai sem régua mesmo com a carta carregada por eixo.
   */
  faixaCanal?: FaixaCanal | null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Casas decimais do domínio Y: 2, ou mais quando a amplitude é menor que um centésimo
 * da unidade — o marco superficial varia milímetros em torno de ~900 m (GUPM-329).
 */
function casasDoDominio(amplitude: number): number {
  if (!(amplitude > 0)) return 2;
  return Math.max(2, Math.ceil(-Math.log10(amplitude)) + 1);
}

/** Arredonda PARA FORA: piso para baixo, teto para cima — o domínio nunca corta dado. */
function pisoDoDominio(n: number, casas: number): number {
  const f = 10 ** casas;
  return Math.floor(n * f) / f;
}
function tetoDoDominio(n: number, casas: number): number {
  const f = 10 ** casas;
  return Math.ceil(n * f) / f;
}

/**
 * Domínio Y de cota — mantém os dados sempre visíveis com folga, inclui os níveis
 * "próximos" e a **Atenção sempre** no topo, e evita que referências muito acima
 * (Emergência, cota de boca) achatem a curva — elas aparecem no zoom-out. Marcos
 * (deslocamento) nunca descem abaixo de zero. Espelha `_m6YRange` do protótipo V8.
 */
export function computeCotaDomain(
  readingValues: number[],
  lines: ReferenceLineSpec[],
  isDisplacement: boolean,
  /** Inclui TODAS as referências (fundo→boca + níveis), não só as próximas —
   *  usado como trava máxima do zoom-out ("enquadramento completo"). */
  includeAllLines = false,
): [number, number] {
  const dataVals = readingValues.filter((v) => Number.isFinite(v));
  const lineVals = lines.map((l) => l.value).filter(Number.isFinite);

  // Sem leituras: usa as linhas de referência (ou fallback) só para não quebrar.
  if (dataVals.length === 0) {
    const vals = lineVals.length ? lineVals : [0, 1];
    const lo0 = Math.min(...vals);
    const hi0 = Math.max(...vals);
    const pad = (hi0 - lo0) * 0.1 || 1;
    return [round2(lo0 - pad), round2(hi0 + pad)];
  }

  const datMin = Math.min(...dataVals);
  const datMax = Math.max(...dataVals);
  const span = datMax - datMin || Math.max(Math.abs(datMax) * 0.02, 1);

  // Referências "próximas" dos dados (janela simétrica de 1,5×span).
  const nearHigh = datMax + span * 1.5;
  const nearLow = datMin - span * 1.5;
  const near = includeAllLines ? lineVals : lineVals.filter((v) => v >= nearLow && v <= nearHigh);

  let lo = datMin;
  let hi = datMax;
  if (near.length) {
    lo = Math.min(lo, Math.min(...near));
    hi = Math.max(hi, Math.max(...near));
  }

  // Atenção sempre visível no topo (mesmo se um pouco além da janela).
  // `startsWith` porque o canal bilateral rotula os dois sentidos — "Atenção (+)"
  // e "Atenção (−)" (GUPM-270). Só o positivo interessa aqui: é o topo.
  const atencao = lines.find((l) => l.kind === 'threshold' && l.label.startsWith('Atenção'));
  if (atencao) hi = Math.max(hi, Math.abs(atencao.value));

  const rng = hi - lo || span;
  const yMin = isDisplacement ? Math.max(0, datMin - rng * 0.12) : lo - rng * 0.12;
  const yMax = hi + rng * 0.15;
  // Arredondar ao mais próximo cortava dado: com `allowDataOverflow` no eixo, o piso
  // 922,9775 → 922,98 deixava fora as campanhas de 922,979x do MS-1 (GUPM-329).
  const casas = casasDoDominio(rng);
  return [pisoDoDominio(yMin, casas), tetoDoDominio(yMax, casas)];
}

/** Monta o modelo do gráfico do instrumento selecionado. */
export function buildInstrumentChartModel(input: BuildChartModelInput): ChartModel {
  const {
    instrument, series, reguaSeries = [], pluviSeries = [], seriesEixoB = [], faixaCanal = null,
  } = input;

  const main = toChartSeries(series);
  const regua = toChartSeries(reguaSeries);
  const pluvi = toChartSeries(pluviSeries);
  const eixoB = toChartSeries(seriesEixoB);

  // Rótulo do X inclui o ano quando a série cruza anos (evita "01/05" ambíguo em
  // séries plurianuais — o caso real da planilha, que vai de 2022 a 2026).
  const allTs = [...main, ...regua, ...pluvi, ...eixoB].map((s) => s.ts).filter(Number.isFinite);
  const multiYear = allTs.length > 0
    && new Date(Math.min(...allTs)).getFullYear() !== new Date(Math.max(...allTs)).getFullYear();

  // União pelo TIMESTAMP exato — nunca colapsa leituras distintas (sub-diárias da
  // ingestão automática, ou mesmo dia/mês de anos diferentes). O eixo X é temporal
  // (numérico), então os pontos ficam posicionados pelo tempo real e os overlays
  // alinham por instante; `connectNulls` liga a linha por cima das lacunas.
  const byTs = new Map<number, ChartSeriesPoint>();
  const ensure = (ts: number): ChartSeriesPoint => {
    let p = byTs.get(ts);
    if (!p) {
      p = {
        date: dateLabel(ts, multiYear), ts, value: null, regua: null, pluviometria: null,
        eixoB: null,
      };
      byTs.set(ts, p);
    }
    return p;
  };
  for (const s of main) ensure(s.ts).value = s.value;
  for (const s of regua) ensure(s.ts).regua = s.value;
  for (const s of pluvi) ensure(s.ts).pluviometria = s.value;
  for (const s of eixoB) ensure(s.ts).eixoB = s.value;
  const points = [...byTs.values()].sort((a, b) => a.ts - b.ts);

  const referenceLines = buildReferenceLines(instrument, faixaCanal);
  const isDisplacement = instrument.type === 'MS' || instrument.type === 'MSE';
  // Os dois eixos dividem a MESMA escala Y (mesma grandeza, mesma unidade): o
  // domínio tem de considerar os dois, senão o eixo B sai do enquadramento.
  const readingVals = [...main, ...eixoB].map((s) => s.value);
  const cotaDomain = computeCotaDomain(readingVals, referenceLines, isDisplacement);
  const fullCotaDomain = computeCotaDomain(readingVals, referenceLines, isDisplacement, true);

  // R-A07: vigência pela leitura mais recente. `ts` inválido (Invalid Date → NaN) não
  // deve marcar "fora de vigência" espuriamente — trata como vigente.
  const latestTs = main.length ? main[main.length - 1]!.ts : null;
  const thresholdsVigentes = latestTs == null || !Number.isFinite(latestTs)
    || latestTs >= instrument.thresholdsValidFrom.getTime();

  return {
    points,
    referenceLines,
    cotaDomain,
    fullCotaDomain,
    hasRegua: regua.length > 0,
    hasPluviometria: pluvi.length > 0,
    hasEixoB: eixoB.length > 0,
    multiYear,
    thresholdsVigentes,
    unit: TYPE_UNIT[instrument.type] ?? '',
  };
}
